import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  MapPin, Plus, Pencil, Trash2, User, Building, Phone, Mail, Save, X, BookOpen
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const EMPTY_FORM = {
  label: "", address_type: "both", name: "", company: "",
  street: "", colonia: "", city: "", state: "", zip: "", phone: "", email: ""
};

const TYPE_LABELS = { remitente: "Remitente", destinatario: "Destinatario", both: "Ambos" };
const TYPE_COLORS = { remitente: "bg-blue-100 text-blue-700", destinatario: "bg-orange-100 text-orange-700", both: "bg-violet-100 text-violet-700" };

const AddressBook = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [colonias, setColonias] = useState([]);

  const fetchAddresses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/addresses`);
      setAddresses(res.data.addresses || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const handleZipChange = async (val) => {
    setForm(f => ({ ...f, zip: val, colonia: "" }));
    if (val.length === 5) {
      try {
        const res = await axios.get(`${API}/zipcode/${val}`);
        if (res.data.colonias?.length > 0) {
          setColonias(res.data.colonias);
          setForm(f => ({ ...f, state: res.data.estado || "", city: res.data.ciudad || res.data.estado || "" }));
          if (res.data.colonias.length === 1) setForm(f => ({ ...f, colonia: res.data.colonias[0] }));
        } else setColonias([]);
      } catch { setColonias([]); }
    } else setColonias([]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/addresses/${editingId}`, form);
        toast.success("Dirección actualizada");
      } else {
        await axios.post(`${API}/addresses`, form);
        toast.success("Dirección guardada");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setColonias([]);
      fetchAddresses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
    setSaving(false);
  };

  const handleEdit = (addr) => {
    setForm({
      label: addr.label, address_type: addr.address_type, name: addr.name,
      company: addr.company || "", street: addr.street, colonia: addr.colonia,
      city: addr.city, state: addr.state, zip: addr.zip, phone: addr.phone, email: addr.email,
    });
    setEditingId(addr.address_id);
    setShowForm(true);
    if (addr.zip?.length === 5) {
      axios.get(`${API}/zipcode/${addr.zip}`).then(r => {
        if (r.data.colonias?.length) setColonias(r.data.colonias);
      }).catch(() => {});
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta dirección?")) return;
    try {
      await axios.delete(`${API}/addresses/${id}`);
      toast.success("Dirección eliminada");
      fetchAddresses();
    } catch { toast.error("Error al eliminar"); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-violet-600" />
            Libreta de Direcciones
          </h2>
          <p className="text-sm text-zinc-500">Guarda tus direcciones frecuentes para enviar más rápido</p>
        </div>
        {!showForm && (
          <Button onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); setColonias([]); }}
            className="bg-violet-600 hover:bg-violet-700" data-testid="add-address-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva dirección
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="border-violet-200 border-2">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">{editingId ? "Editar dirección" : "Nueva dirección"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label>Etiqueta *</Label>
                  <Input placeholder="Casa, Oficina, Bodega..." value={form.label}
                    onChange={e => setForm(f => ({...f, label: e.target.value}))} required className="mt-1"
                    data-testid="address-label-input" />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <select value={form.address_type} onChange={e => setForm(f => ({...f, address_type: e.target.value}))}
                    className="w-full mt-1 h-10 px-3 border border-zinc-200 rounded-md text-sm"
                    data-testid="address-type-select">
                    <option value="both">Remitente y Destinatario</option>
                    <option value="remitente">Solo Remitente</option>
                    <option value="destinatario">Solo Destinatario</option>
                  </select>
                </div>
                <div>
                  <Label>Código Postal *</Label>
                  <Input placeholder="06600" maxLength={5} value={form.zip}
                    onChange={e => handleZipChange(e.target.value)} required className="mt-1"
                    data-testid="address-zip-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre completo *</Label>
                  <Input placeholder="Juan Pérez" value={form.name}
                    onChange={e => setForm(f => ({...f, name: e.target.value}))} required className="mt-1"
                    data-testid="address-name-input" />
                </div>
                <div>
                  <Label>Empresa</Label>
                  <Input placeholder="Mi Empresa S.A." value={form.company}
                    onChange={e => setForm(f => ({...f, company: e.target.value}))} className="mt-1"
                    data-testid="address-company-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Calle y número *</Label>
                  <Input placeholder="Av. Reforma #123" value={form.street}
                    onChange={e => setForm(f => ({...f, street: e.target.value}))} required className="mt-1"
                    data-testid="address-street-input" />
                </div>
                <div>
                  <Label>Colonia *</Label>
                  {colonias.length > 0 ? (
                    <select value={form.colonia} onChange={e => setForm(f => ({...f, colonia: e.target.value}))}
                      className="w-full mt-1 h-10 px-3 border border-zinc-200 rounded-md text-sm" required
                      data-testid="address-colonia-select">
                      <option value="">Selecciona una colonia</option>
                      {colonias.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <Input placeholder="Del Valle" value={form.colonia}
                      onChange={e => setForm(f => ({...f, colonia: e.target.value}))} required className="mt-1"
                      data-testid="address-colonia-input" />
                  )}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Ciudad *</Label>
                  <Input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} required className="mt-1" />
                </div>
                <div>
                  <Label>Estado *</Label>
                  <Input value={form.state} onChange={e => setForm(f => ({...f, state: e.target.value}))} required className="mt-1" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Teléfono *</Label>
                  <Input placeholder="55 1234 5678" value={form.phone}
                    onChange={e => setForm(f => ({...f, phone: e.target.value}))} required className="mt-1"
                    data-testid="address-phone-input" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" placeholder="correo@ejemplo.com" value={form.email}
                    onChange={e => setForm(f => ({...f, email: e.target.value}))} required className="mt-1"
                    data-testid="address-email-input" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="save-address-btn">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : (editingId ? "Actualizar" : "Guardar")}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); setColonias([]); }}>
                  <X className="w-4 h-4 mr-2" /> Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {addresses.length === 0 && !showForm ? (
        <Card className="border-dashed border-2 border-zinc-300">
          <CardContent className="py-16 text-center">
            <MapPin className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-zinc-600 mb-2">Sin direcciones guardadas</h3>
            <p className="text-zinc-400 mb-6">Agrega tus direcciones frecuentes para enviar más rápido</p>
            <Button onClick={() => { setForm(EMPTY_FORM); setShowForm(true); }} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="w-4 h-4 mr-2" /> Agregar primera dirección
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {addresses.map(addr => (
            <Card key={addr.address_id} className="hover:shadow-lg transition-shadow group" data-testid={`address-card-${addr.address_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm">{addr.label}</h3>
                      <Badge variant="secondary" className={`text-xs ${TYPE_COLORS[addr.address_type] || TYPE_COLORS.both}`}>
                        {TYPE_LABELS[addr.address_type] || "Ambos"}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleEdit(addr)} className="p-1.5 hover:bg-zinc-100 rounded-lg" data-testid={`edit-address-${addr.address_id}`}>
                      <Pencil className="w-3.5 h-3.5 text-zinc-500" />
                    </button>
                    <button onClick={() => handleDelete(addr.address_id)} className="p-1.5 hover:bg-red-50 rounded-lg" data-testid={`delete-address-${addr.address_id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5 text-sm text-zinc-600">
                  <p className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-zinc-400" /> {addr.name}</p>
                  {addr.company && <p className="flex items-center gap-2"><Building className="w-3.5 h-3.5 text-zinc-400" /> {addr.company}</p>}
                  <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-zinc-400" /> {addr.street}, {addr.colonia}</p>
                  <p className="text-zinc-400 pl-5.5">{addr.city}, {addr.state} {addr.zip}</p>
                  <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-zinc-400" /> {addr.phone}</p>
                  <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-zinc-400" /> {addr.email}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressBook;
